import React, { useEffect, useState } from "react";
import api, { formatPHP, formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Plus, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function FinalPay() {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState({});
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ employee_id: "", separation_date: new Date().toISOString().slice(0, 10), separation_type: "resignation", separation_cause: "", years_of_service: "", unused_leave_days: "", override_amount: "", override_reason: "" });

    const load = async () => {
        const [r, emp] = await Promise.all([api.get("/payroll/final-pay"), api.get("/hris/employees", { params: { limit: 500 } })]);
        const m = {}; (emp.data.items || []).forEach((e) => m[e.id] = `${e.last_name}, ${e.first_name} (${e.employee_no})`);
        setEmployees(m);
        setItems(r.data || []);
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            const payload = { ...form };
            if (!payload.override_amount) delete payload.override_amount;
            else payload.override_amount = Number(payload.override_amount);
            if (payload.years_of_service === "") delete payload.years_of_service;
            else payload.years_of_service = Number(payload.years_of_service);
            payload.unused_leave_days = Number(payload.unused_leave_days || 0);
            if (payload.separation_type !== "authorized_cause") payload.separation_cause = "";
            await api.post("/payroll/final-pay", payload);
            toast.success("Final pay computed");
            setOpen(false); setForm({ ...form, employee_id: "", separation_cause: "", years_of_service: "", unused_leave_days: "", override_amount: "", override_reason: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const approve = async (id) => {
        try { await api.post(`/payroll/final-pay/${id}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                title="Final Pay"
                subtitle="System-computed final pay with manual override and approval trail. Tied to clearance and asset return."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-finalpay-btn"><Plus className="size-4" /> New Final Pay</button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Compute Final Pay</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div>
                                    <Label>Employee</Label>
                                    <EmployeePicker value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="fp-employee" />
                                </div>
                                <div><Label>Separation Date</Label><Input type="date" value={form.separation_date} onChange={(e) => setForm({ ...form, separation_date: e.target.value })} data-testid="fp-sep-date" /></div>
                                <div>
                                    <Label>Separation Type</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.separation_type} onChange={(e) => setForm({ ...form, separation_type: e.target.value })} data-testid="fp-sep-type">
                                        <option value="resignation">Resignation</option>
                                        <option value="end_of_contract">End of Contract</option>
                                        <option value="authorized_cause">Authorized-cause Termination</option>
                                        <option value="retirement">Retirement (RA 7641)</option>
                                        <option value="termination">Termination (just cause)</option>
                                    </select>
                                </div>
                                {form.separation_type === "authorized_cause" && (
                                    <div>
                                        <Label>Authorized Cause</Label>
                                        <select className="w-full h-10 px-3 rounded-md border border-border" value={form.separation_cause} onChange={(e) => setForm({ ...form, separation_cause: e.target.value })} data-testid="fp-sep-cause">
                                            <option value="">Select cause…</option>
                                            <option value="redundancy">Redundancy (1 mo/yr)</option>
                                            <option value="labor_saving_device">Labor-saving device (1 mo/yr)</option>
                                            <option value="retrenchment">Retrenchment (½ mo/yr)</option>
                                            <option value="closure">Closure, not due to losses (½ mo/yr)</option>
                                            <option value="disease">Disease (½ mo/yr)</option>
                                        </select>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label>Years of Service</Label><Input type="number" step="0.1" value={form.years_of_service} onChange={(e) => setForm({ ...form, years_of_service: e.target.value })} placeholder="auto from hire date" data-testid="fp-years" /></div>
                                    <div><Label>Unused Leave Days</Label><Input type="number" step="0.5" value={form.unused_leave_days} onChange={(e) => setForm({ ...form, unused_leave_days: e.target.value })} placeholder="SIL / VL to encash" data-testid="fp-leave-days" /></div>
                                </div>
                                <div><Label>Override Amount (optional)</Label><Input type="number" value={form.override_amount} onChange={(e) => setForm({ ...form, override_amount: e.target.value })} placeholder="Leave blank to use computed amount" data-testid="fp-override" /></div>
                                <div><Label>Override Reason</Label><Input value={form.override_reason} onChange={(e) => setForm({ ...form, override_reason: e.target.value })} placeholder="Required if overriding" data-testid="fp-reason" /></div>
                            </div>
                            <DialogFooter>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="fp-submit">Compute</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <TableWrap>
                <thead>
                    <tr>
                        <th>Employee</th>
                        <th>Separation</th>
                        <th className="num">Computed</th>
                        <th className="num">Override</th>
                        <th>Clearance</th>
                        <th>Assets</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((it) => (
                        <tr key={it.id} data-row-id={it.id} data-testid={`fp-row-${it.id}`}>
                            <td>{employees[it.employee_id] || it.employee_id?.slice(0, 8)}</td>
                            <td className="font-mono">{it.separation_date}</td>
                            <td className="num">{formatPHP(it.computed_amount)}</td>
                            <td className="num">{it.override_amount ? formatPHP(it.override_amount) : "—"}</td>
                            <td>{it.clearance_passed ? <span className="badge badge-approved">passed</span> : <span className="badge badge-draft">pending</span>}</td>
                            <td>{it.assets_returned ? <span className="badge badge-approved">returned</span> : <span className="badge badge-draft">pending</span>}</td>
                            <td><StatusBadge value={it.status} /></td>
                            <td>
                                {it.status !== "approved" && <button onClick={() => approve(it.id)} className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white flex items-center gap-1" data-testid={`fp-approve-${it.id}`}><Check className="size-3" /> Approve</button>}
                            </td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={8}><Empty title="No final pay records" /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
